import { useEffect, useRef, useState } from 'react';
import './TeacherDemonstrations.css';
import demonstrationProjects from '../shared/demonstration-projects.json';
const projects = Object.fromEntries(demonstrationProjects.map(item => [item.id, item.section]));
export type DemonstrationWorkflowStatus = { contentId: string; available: boolean; open: boolean; shotId: string };
export const workflowStatusEvent = 'duanju-demo-workflow-status';
export const workflowToggleEvent = 'duanju-demo-workflow-toggle';
export function TeacherDemonstrations({ userId, kind }: { userId: string; kind: 'scripts' | 'dramas' }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const base = `/teacher/${userId}/${kind}`;
  const toInner = () => {
    const suffix = location.pathname.slice(base.length);
    return suffix ? `/c${suffix}${location.search}` : `/${kind === 'scripts' ? 'script' : 'short'}/my${location.search}`;
  };
  const [src] = useState(() => `/demonstration.html?kind=${kind}&route=${encodeURIComponent(toInner())}`);
  useEffect(() => {
    let workflow: DemonstrationWorkflowStatus | null = null;
    const publishWorkflow = () => {
      const available = Boolean(kind === 'dramas' && workflow?.available && location.pathname === `${base}/${workflow.contentId}/render`);
      window.dispatchEvent(new CustomEvent(workflowStatusEvent, { detail: { ...workflow, available, open: available && Boolean(workflow?.open) } }));
    };
    const receive = (event: MessageEvent) => {
      if (event.source !== frame.current?.contentWindow || event.origin !== location.origin) return;
      if (event.data?.type === 'duanju-demo-workflow-state') {
        const { contentId, available, open, shotId } = event.data;
        if (kind !== 'dramas' || typeof contentId !== 'string' || projects[contentId] !== 'dramas' || typeof available !== 'boolean' || typeof open !== 'boolean') return;
        if (!available && workflow?.contentId !== contentId) return;
        workflow = { contentId, available, open, shotId: typeof shotId === 'string' ? shotId : '' };
        publishWorkflow();
        return;
      }
      if (event.data?.type !== 'duanju-demo-route' || typeof event.data.path !== 'string') return;
      const inner = new URL(event.data.path, location.origin);
      const parts = inner.pathname.split('/');
      const target = parts[1] === 'c' && projects[parts[2]] ? `/teacher/${userId}/${projects[parts[2]]}/${parts.slice(2).join('/')}${inner.search}` : inner.pathname === `/${kind === 'scripts' ? 'script' : 'short'}/my` ? `${base}${inner.search}` : null;
      if (target && target !== location.pathname + location.search) {
        if (event.data.replace) history.replaceState(null, '', target); else history.pushState(null, '', target);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    };
    const restore = () => {
      if (location.pathname === base || location.pathname.startsWith(base + '/')) frame.current?.contentWindow?.postMessage({ type: 'duanju-demo-navigate', path: toInner() }, location.origin);
      publishWorkflow();
    };
    const toggleWorkflow = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (!workflow?.available || detail?.contentId !== workflow.contentId || location.pathname !== `${base}/${workflow.contentId}/render`) return;
      frame.current?.contentWindow?.postMessage({ type: workflowToggleEvent, contentId: workflow.contentId, ...(typeof detail.open === 'boolean' ? { open: detail.open } : {}) }, location.origin);
    };
    window.addEventListener('message', receive); window.addEventListener('popstate', restore);
    window.addEventListener(workflowToggleEvent, toggleWorkflow);
    return () => {
      window.removeEventListener('message', receive); window.removeEventListener('popstate', restore);
      window.removeEventListener(workflowToggleEvent, toggleWorkflow);
      workflow = null;
      publishWorkflow();
    };
  }, [base, kind, userId]);
  return <iframe ref={frame} className="teacher-demonstration-frame" title={kind === 'scripts' ? '剧本示范工作台' : '短剧示范工作台'} src={src} sandbox="allow-scripts allow-same-origin allow-downloads allow-popups allow-modals" />;
}
